import {getLogStatus} from '@/modules/system-logs/functions'
import {getLogName} from '@/modules/system-logs/log-message'
import {useEventLogContext} from '@/shared/contexts/event-log'
import {Badge, Flex, Icon, Text} from '@chakra-ui/react'
import {getMonthDayTimeFormatter} from '@cypro/ui/utils'
import {useTranslate} from '@tolgee/react'
import {LuCircleAlert, LuCircleCheck, LuCircleX, LuTriangleAlert} from 'react-icons/lu'

// config details for the various types of status badges
const STATUS_CONFIG = {
  OK: {
    color: 'green.fg',
    bgColor: 'green.subtle',
    icon: LuCircleCheck,
  },
  FAILED: {
    color: 'red.fg',
    bgColor: 'red.subtle',
    icon: LuCircleX,
  },
  INFO: {
    color: 'blue.fg',
    bgColor: 'blue.subtle',
    icon: LuCircleAlert,
  },
  WARNING: {
    color: 'yellow.fg',
    bgColor: 'yellow.subtle',
    icon: LuTriangleAlert,
  },
} as const

const DEFAULT_CONFIG = {
  color: 'gray.fg',
  bgColor: 'gray.subtle',
  icon: LuCircleX,
}

export function LogDrawerHeader() {
  const {t} = useTranslate('common')
  const {t: tE} = useTranslate('events')
  const {log} = useEventLogContext()

  const getStatusBadge = (status: 'OK' | 'FAILED' | 'WARNING' | 'INFO' | null) => {
    const config = status ? (STATUS_CONFIG[status] ?? DEFAULT_CONFIG) : DEFAULT_CONFIG

    return (
      <Badge
        display="inline-flex"
        alignItems="center"
        gap={1}
        color={config.color}
        backgroundColor={config.bgColor}
        variant="subtle"
        py={1}
        px={2}
        rounded="md"
      >
        <Text fontSize="sm">{t(`log_status_${getLogStatus(log)}`)}</Text>
        <Icon as={config.icon} boxSize={4} />
      </Badge>
    )
  }

  return (
    <Flex justify="space-between" marginY={10}>
      <Flex direction="column" gap={3}>
        <Text fontSize="2xl" fontWeight="semibold" overflowWrap="anywhere">
          {getLogName(log, t, tE)}
        </Text>
        <Text fontSize="md" color="fg.muted">
          {formatDayKey(log.performedAt)}
        </Text>
        {Boolean(log.details?.reason) && (
          <Text fontSize="sm" whiteSpace="pre-line" overflowWrap="anywhere">
            <Text as="span" fontWeight="semibold">
              {t('reason')}:
            </Text>{' '}
            {log.details?.reason}
          </Text>
        )}
      </Flex>
      <Flex direction="column" justify="flex-start">
        {getStatusBadge(getLogStatus(log))}
      </Flex>
    </Flex>
  )
}

const formatDayKey = (value: number) => {
  return getMonthDayTimeFormatter().format(value)
}
